/**
 * Een eenvoudige modale dialoog: het overlay, Escape om te sluiten en de focus die
 * bij het openen naar het paneel gaat.
 *
 * Gebruikt voor het stap-menu, de reworkmelding, het splitsen in karren en het
 * on-hold zetten — overal waar het design een menu "naar voren laat komen".
 */
import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  titel: string;
  children: ReactNode;
  voet?: ReactNode;
  onSluiten: () => void;
  breedte?: number;
}

export function Dialoog({ titel, children, voet, onSluiten, breedte }: Props) {
  const paneel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    paneel.current?.focus();
    const opToets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSluiten();
    };
    window.addEventListener('keydown', opToets);
    return () => window.removeEventListener('keydown', opToets);
  }, [onSluiten]);

  return (
    <div className="hs-overlay" onClick={onSluiten} role="presentation">
      <div
        ref={paneel}
        className="hs-dialoog"
        style={breedte ? { maxWidth: breedte } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={titel}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hs-dialoog__kop">{titel}</div>
        <div className="hs-dialoog__lijf">{children}</div>
        {voet ? <div className="hs-dialoog__voet">{voet}</div> : null}
      </div>
    </div>
  );
}

/** Een aan/uit-schakelaar met label, zoals in het stap-menu. */
export function Schakelaar({
  label,
  aan,
  onWissel,
  uitleg,
  uitgeschakeld,
}: {
  label: string;
  aan: boolean;
  onWissel: (aan: boolean) => void;
  uitleg?: string;
  uitgeschakeld?: boolean;
}) {
  return (
    <div className="hs-schakel">
      <span>
        <span style={{ fontWeight: 500 }}>{label}</span>
        {uitleg ? (
          <span className="hs-muted" style={{ display: 'block', fontSize: 11 }}>
            {uitleg}
          </span>
        ) : null}
      </span>
      <button
        type="button"
        className={`hs-schakel__spoor${aan ? ' aan' : ''}`}
        onClick={() => onWissel(!aan)}
        disabled={uitgeschakeld}
        role="switch"
        aria-checked={aan}
        aria-label={label}
      />
    </div>
  );
}
